import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Link } from "react-router-dom";
import { MessageCircle, Send, Sparkles, X } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/lib/auth";
import { streamAI } from "./stream";

const Bubble = ({ m }) => (
  <div className={`max-w-[88%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "whitespace-pre-wrap" : ""} ${m.role === "user" ? "ml-auto bg-[var(--cyan)] text-[#03141A]" : "bg-white/[0.05] border border-[var(--line)]"}`}
    data-testid={`ai-msg-${m.role}`}>{m.role === "user" ? m.text : <div className="ai-md">{m.text ? <ReactMarkdown>{m.text}</ReactMarkdown> : "…"}</div>}</div>
);

const SUGGEST = ["What are today's best data picks?", "Explain how sports spreads work", "How is my bet size calculated?"];

export const AiChat = () => {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState({});
  const [msgs, setMsgs] = useState([]);
  const [sid, setSid] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [quota, setQuota] = useState(null);
  const end = useRef(null);
  useEffect(() => {
    const h = (e) => { const d = e.detail || {}; if (JSON.stringify(d.context || {}) !== JSON.stringify(ctx)) { setMsgs([]); setSid(null); } setCtx(d.context || {}); setOpen(true); if (d.prompt) setText(d.prompt); };
    window.addEventListener("ai-open", h);
    return () => window.removeEventListener("ai-open", h);
  }, [ctx]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);
  const send = async (q) => {
    const message = (q ?? text).trim();
    if (!message || busy) return;
    setText(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", text: message }, { role: "assistant", text: "" }]);
    const add = (t) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + t }; return c; });
    try {
      await streamAI("/ai/chat", { message, session_id: sid, mode: ctx.handbook_topic ? "tutor" : "chat", context: ctx }, (ev) => {
        if (ev.session_id) setSid(ev.session_id);
        if (ev.delta) add(ev.delta);
        if (ev.error) add(ev.error);
        if (ev.done) setQuota(ev);
      });
    } catch (e) { add(e.message); } finally { setBusy(false); }
  };
  const label = ctx.handbook_topic ? "Handbook tutor" : ctx.slug ? "Asking about this market" : "Market assistant";
  return (
    <>
      <button onClick={() => { setCtx({}); setOpen(true); }} data-testid="ai-fab"
        aria-label="Ask AI" className="fixed z-40 right-3 bottom-[5.5rem] lg:bottom-6 h-11 w-11 sm:w-auto justify-center sm:px-4 rounded-full bg-[var(--violet)] text-white font-semibold shadow-lg shadow-violet-900/40 flex items-center gap-2 hover:-translate-y-0.5 transition-transform">
        <Sparkles className="w-4 h-4" /><span className="text-sm hidden sm:inline">Ask AI</span></button>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md bg-[var(--bg)] border-[var(--line)] flex flex-col p-0" data-testid="ai-sheet">
          <SheetHeader className="text-left p-5 pb-3 border-b border-[var(--line)]">
            <SheetTitle className="font-head text-slate-100 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[var(--violet)]" />{label}</SheetTitle>
            <SheetDescription className="text-xs">ChatGPT (GPT 6 Sol) reading live app data. Educational, not financial advice.
              {quota && <span data-testid="ai-quota"> · {quota.limit == null ? "unlimited" : `${quota.remaining} of ${quota.limit} left today`}</span>}</SheetDescription>
          </SheetHeader>
          {!profile ? <div className="p-5 text-sm text-slate-400" data-testid="ai-signin">Sign in to chat. Free accounts get 5 questions a day. <Link to="/account" onClick={() => setOpen(false)} className="text-[var(--cyan)]">Sign in</Link></div> : <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="ai-messages">
              {msgs.length === 0 && <div className="space-y-2">{SUGGEST.map((s) => <button key={s} onClick={() => send(s)} className="block w-full text-left text-sm rounded-xl border border-[var(--line)] px-3 py-2 hover:border-[var(--violet)]" data-testid="ai-suggestion">{s}</button>)}</div>}
              {msgs.map((m, i) => <Bubble key={i} m={m} />)}
              <div ref={end} />
            </div>
            <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-[var(--line)] flex gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Ask about any market, pick or lesson…" maxLength={2000}
                className="flex-1 h-11 rounded-xl bg-[var(--card)] border border-[var(--line)] px-3 text-sm outline-none focus:border-[var(--violet)]" data-testid="ai-input" />
              <button disabled={busy || !text.trim()} className="h-11 w-11 rounded-xl bg-[var(--violet)] text-white flex items-center justify-center disabled:opacity-40" data-testid="ai-send"><Send className="w-4 h-4" /></button>
              {ctx.slug || ctx.handbook_topic ? <button type="button" onClick={() => { setCtx({}); setMsgs([]); setSid(null); }} className="h-11 w-11 rounded-xl border border-[var(--line)] flex items-center justify-center" title="Clear context" data-testid="ai-clear-context"><X className="w-4 h-4" /></button> : null}
            </form></>}
        </SheetContent>
      </Sheet>
    </>
  );
};
